import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, Loader2, GraduationCap, Upload, 
  AlertCircle, Clock, XCircle, FileCheck, Shield, Send,
  Mail, ClipboardCheck, UserCheck, FileQuestion
} from 'lucide-react';
import api, { uploadAPI } from '../lib/api';

const DOCUMENT_TYPES = [
  {
    key: 'government_id',
    title: 'Government-Issued ID',
    description: 'Valid passport, driver\'s license, or national ID card',
    required: true,
    icon: FileCheck
  },
  {
    key: 'proof_of_education',
    title: 'Proof of Education',
    description: 'Degree certificate, transcript, or enrollment verification',
    required: true,
    icon: GraduationCap
  },
  {
    key: 'police_check',
    title: 'Police Background Check',
    description: 'Recent police clearance certificate (within 6 months)',
    required: true,
    icon: Shield
  },
  {
    key: 'certification',
    title: 'Teaching Certifications',
    description: 'Any relevant teaching or tutoring certifications (optional)',
    required: false,
    icon: FileText
  },
  {
    key: 'resume',
    title: 'Resume / CV',
    description: 'Your professional resume or curriculum vitae (optional)',
    required: false,
    icon: FileText
  }
];

const TutorDocumentsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await api.get('/tutor/onboarding-status');
      setDocuments(response.data.documents || []);
      setOnboardingStatus(response.data);
      
      // Check if already submitted
      if (response.data.profile?.application_submitted || 
          response.data.onboarding_status === 'under_review' ||
          response.data.onboarding_status === 'approved') {
        setApplicationSubmitted(true);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const getDocumentByType = (type) => {
    return documents.find(doc => doc.document_type === type);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />Under Review</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">Not Uploaded</Badge>;
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!file) return;

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, and PDF files are allowed');
      return;
    }

    setUploading(prev => ({ ...prev, [docType]: true }));

    try {
      // Upload file first
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await uploadAPI.uploadFile(formData);
      const fileUrl = uploadResponse.data.url;

      // Then register the document
      await api.post('/tutor/documents/upload', {
        document_type: docType,
        file_url: fileUrl,
        file_name: file.name
      });

      toast.success('Document uploaded successfully');
      await loadData(); // Refresh
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const handleSubmitApplication = async () => {
    // Validate all requirements
    if (!onboardingStatus?.agreement_signed) {
      toast.error('Please sign the tutor agreement first');
      return;
    }

    const requiredDocs = ['government_id', 'proof_of_education', 'police_check'];
    const uploadedTypes = documents.map(d => d.document_type);
    const missingDocs = requiredDocs.filter(d => !uploadedTypes.includes(d));

    if (missingDocs.length > 0) {
      toast.error(`Please upload all required documents: ${missingDocs.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/tutor/submit-application');
      
      toast.success('Application submitted successfully!');
      if (response.data.confirmation_email_sent) {
        toast.success('Confirmation email sent to your inbox');
      }
      
      setApplicationSubmitted(true);
      await loadData(); // Refresh status
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const allRequiredUploaded = onboardingStatus?.all_required_uploaded;
  const allApproved = onboardingStatus?.all_documents_approved;
  const agreementSigned = onboardingStatus?.agreement_signed;
  const canSubmit = agreementSigned && allRequiredUploaded && !applicationSubmitted;

  // Application Submitted - Show Completion State
  if (applicationSubmitted) {
    return (
      <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="tutor-submission-complete">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <GraduationCap className="h-10 w-10 text-indigo-700" />
              <span className="font-heading text-2xl font-semibold text-slate-900">
                Gifted Academy Tutors
              </span>
            </div>
          </div>

          {/* Success Card */}
          <Card className="shadow-lg border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardContent className="pt-8 pb-10">
              {/* Success Icon */}
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 ring-4 ring-green-50">
                  <CheckCircle className="h-14 w-14 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Application Submitted!</h2>
                <p className="text-slate-600">Your tutor application is now under review.</p>
              </div>

              {/* Checklist */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Profile Submitted</p>
                    <p className="text-sm text-slate-500">Your tutor profile information has been received</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Agreement Signed</p>
                    <p className="text-sm text-slate-500">Tutor agreement signed on {onboardingStatus?.agreement?.signed_at ? new Date(onboardingStatus.agreement.signed_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Upload className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Documents Uploaded</p>
                    <p className="text-sm text-slate-500">{documents.length} document(s) received for verification</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Confirmation Emails Sent</p>
                    <p className="text-sm text-slate-500">Agreement copy and confirmation sent to your email</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserCheck className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Under Review</p>
                    <p className="text-sm text-slate-500">Our team will review your application within 2-5 business days</p>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-slate-50 rounded-xl p-5 text-center">
                <h4 className="font-medium text-slate-800 mb-2">What Happens Next?</h4>
                <p className="text-sm text-slate-600 mb-3">
                  Our team will carefully review your application and documents. 
                  You will receive an email notification once your application has been processed.
                </p>
                <p className="text-sm text-slate-500">
                  Questions? Contact us at <a href="mailto:contact@giftedacademytutors.ca" className="text-indigo-600 hover:underline">contact@giftedacademytutors.ca</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="tutor-documents-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <GraduationCap className="h-10 w-10 text-indigo-700" />
            <span className="font-heading text-2xl font-semibold text-slate-900">
              Gifted Academy Tutors
            </span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Verification Documents</h1>
          <p className="text-slate-600">Upload required documents for identity and background verification</p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${agreementSigned ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              {agreementSigned ? <CheckCircle className="h-4 w-4" /> : <FileQuestion className="h-4 w-4" />}
              Agreement
            </div>
            <div className="w-8 h-0.5 bg-slate-200"></div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${allRequiredUploaded ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`}>
              {allRequiredUploaded ? <CheckCircle className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
              Documents
            </div>
            <div className="w-8 h-0.5 bg-slate-200"></div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${applicationSubmitted ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              <Send className="h-4 w-4" />
              Submit
            </div>
          </div>
        </div>

        {/* Status Banner */}
        {onboardingStatus && (
          <Card className={`mb-6 ${
            allApproved ? 'bg-green-50 border-green-200' : 
            allRequiredUploaded ? 'bg-blue-50 border-blue-200' : 
            'bg-amber-50 border-amber-200'
          }`}>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                {allApproved ? (
                  <>
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">All Documents Approved!</p>
                      <p className="text-sm text-green-700">Your verification is complete.</p>
                    </div>
                  </>
                ) : allRequiredUploaded ? (
                  <>
                    <CheckCircle className="h-6 w-6 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-800">All Required Documents Uploaded</p>
                      <p className="text-sm text-blue-700">You can now submit your application for review.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-6 w-6 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Documents Required</p>
                      <p className="text-sm text-amber-700">Please upload all required documents to proceed.</p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agreement Status */}
        {!agreementSigned && (
          <Card className="mb-6 bg-red-50 border-red-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Agreement Not Signed</p>
                    <p className="text-sm text-red-700">Please sign the tutor agreement first.</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/tutor/agreement')}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  Sign Agreement
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Cards */}
        <div className="space-y-4 mb-8">
          {DOCUMENT_TYPES.map((docType) => {
            const existingDoc = getDocumentByType(docType.key);
            const Icon = docType.icon;
            const isUploading = uploading[docType.key];

            return (
              <Card 
                key={docType.key}
                className={`transition-all ${
                  existingDoc?.status === 'approved' ? 'border-green-200 bg-green-50/30' :
                  existingDoc?.status === 'rejected' ? 'border-red-200 bg-red-50/30' :
                  existingDoc ? 'border-blue-200 bg-blue-50/30' : ''
                }`}
                data-testid={`document-card-${docType.key}`}
              >
                <CardContent className="py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        existingDoc?.status === 'approved' ? 'bg-green-100' :
                        existingDoc?.status === 'rejected' ? 'bg-red-100' :
                        existingDoc ? 'bg-blue-100' : 'bg-slate-100'
                      }`}>
                        <Icon className={`h-6 w-6 ${
                          existingDoc?.status === 'approved' ? 'text-green-600' :
                          existingDoc?.status === 'rejected' ? 'text-red-600' :
                          existingDoc ? 'text-blue-600' : 'text-slate-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{docType.title}</h3>
                          {docType.required ? (
                            <Badge variant="outline" className="text-xs">Required</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-slate-500">Optional</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{docType.description}</p>
                        
                        {existingDoc && (
                          <div className="flex items-center gap-3">
                            {getStatusBadge(existingDoc.status)}
                            <span className="text-xs text-slate-500">{existingDoc.file_name}</span>
                          </div>
                        )}
                        
                        {existingDoc?.admin_notes && existingDoc.status === 'rejected' && (
                          <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                            <strong>Feedback:</strong> {existingDoc.admin_notes}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      <input
                        type="file"
                        id={`file-${docType.key}`}
                        className="hidden"
                        accept=".jpg,.jpeg,.png,.webp,.pdf"
                        onChange={(e) => handleFileUpload(docType.key, e.target.files[0])}
                        disabled={isUploading}
                      />
                      <Button
                        variant={existingDoc ? "outline" : "default"}
                        size="sm"
                        onClick={() => document.getElementById(`file-${docType.key}`).click()}
                        disabled={isUploading}
                        data-testid={`upload-btn-${docType.key}`}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Uploading...
                          </>
                        ) : existingDoc ? (
                          <>
                            <Upload className="h-4 w-4 mr-1" />
                            Replace
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Submit Application Button */}
        <div className="mb-8">
          <Card className={`${canSubmit ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}>
            <CardContent className="py-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Submit Your Application</h3>
                <p className="text-sm text-slate-600 mb-4">
                  {canSubmit 
                    ? "All requirements met! Submit your application for review." 
                    : "Complete all requirements above to submit your application."}
                </p>
                
                {/* Requirements Checklist */}
                <div className="flex justify-center gap-6 mb-4 text-sm">
                  <div className={`flex items-center gap-1.5 ${agreementSigned ? 'text-green-600' : 'text-slate-400'}`}>
                    {agreementSigned ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    Agreement Signed
                  </div>
                  <div className={`flex items-center gap-1.5 ${allRequiredUploaded ? 'text-green-600' : 'text-slate-400'}`}>
                    {allRequiredUploaded ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    Documents Uploaded
                  </div>
                </div>

                <Button
                  size="lg"
                  onClick={handleSubmitApplication}
                  disabled={!canSubmit || submitting}
                  className="rounded-full px-8"
                  data-testid="submit-application-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Submit Application
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-slate-500 mt-3">
                  By submitting, you confirm all information is accurate and complete.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Help Text */}
        <div className="p-4 bg-slate-100 rounded-xl">
          <h4 className="font-medium text-slate-800 mb-2">Document Guidelines</h4>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• Documents must be clear and readable</li>
            <li>• Accepted formats: JPEG, PNG, WebP, PDF</li>
            <li>• Maximum file size: 10MB per document</li>
            <li>• Police background check must be issued within the last 6 months</li>
            <li>• All information must match your profile details</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TutorDocumentsPage;
